import {
  InfoFeatureType,
  TAuditChangeLog
} from "change-events/change-event-polling";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { GET_FEATURE_INSTANCE_PLANNING_DATA } from "entity-app/graphQL/ciq-feature-queries";
import { getGQLQueryData } from "entity-app/services";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";
import { hasTheFeature } from "entity-app/utils/utils";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

type UUID = string;
type ISO8601Date = string;

interface FeatureInstance {
  id: UUID;
  title: string;
  feature_type_id: number;
}

export interface WorkflowInstanceOffset {
  id: UUID;
  name: string;
  duration: number;
}

export interface WorkflowInstanceMilestone {
  id: UUID;
  name: string;
  planned_date: ISO8601Date;
  sequence_no: number;
  hidden: boolean;
}

interface FeatureWorkflowInstance {
  id: UUID;
  manual_entry_date: ISO8601Date;
}

interface TargetFeatureInstance extends FeatureInstance {
  project_id: UUID;
  feature_workflow_instance: FeatureWorkflowInstance;
  workflow_instance_milestones: WorkflowInstanceMilestone[];
  workflow_instance_offsets: WorkflowInstanceOffset[];
}

interface Link {
  id: UUID;
  target_activities_instance: null;
  target_feature_instance: TargetFeatureInstance;
}

interface NestedFeatureInstance extends FeatureInstance {
  workflow_instance_offsets: WorkflowInstanceOffset[];
  workflow_instance_milestones: WorkflowInstanceMilestone[];
  links_as_source: Link[];
}

export interface RootFeatureInstance extends FeatureInstance {
  links_as_source: {
    target_feature_instance: NestedFeatureInstance;
  }[];
}

interface PlanningLink extends Link {
  workflow_instance_milestones: WorkflowInstanceMilestone[];
  workflow_instance_offsets: WorkflowInstanceOffset[];
  links_as_source: {
    target_feature_instance?: NestedFeatureInstance;
    target_activities_instance: {
      id: string;
      title: string;
      feature_activities: {
        start_date: string;
        end_date: string;
        source_task_id: string;
        type: number;
      }[];
    };
    link_type: number;
  }[];
  feature_workflow_instance: FeatureWorkflowInstance;
  governing_link: boolean;
}

export type LinksForFeature = {
  [K in keyof typeof FeatureTypes]?: PlanningLink;
};

function extractTargetFeatureInstances(data: any): any[] {
  const instances: any[] = [];

  function traverse(obj: any) {
    if (typeof obj !== "object" || obj === null) return;

    if (obj.target_feature_instance) {
      const reStructuredObj = { ...obj, ...obj.target_feature_instance };
      delete reStructuredObj.target_feature_instance;
      instances.push(reStructuredObj);
    }

    if (Array.isArray(obj)) {
      obj.forEach(traverse);
    } else {
      Object.values(obj).forEach(traverse);
    }
  }

  traverse(data);
  return instances;
}

function formatWFInstanceMilestones(
  workflowInstanceMilestones: WorkflowInstanceMilestone[]
) {
  // remove hidden milestones and sort by sequence_no
  const visibleMilestones = workflowInstanceMilestones.filter(
    (milestone) => !milestone.hidden
  );
  visibleMilestones.sort((a, b) => a.sequence_no - b.sequence_no);
  return visibleMilestones;
}

const allImpactingFeatureTypes: any = [
  InfoFeatureType.designPackage,
  InfoFeatureType.bidPackage,
  InfoFeatureType.planningActivity,
  InfoFeatureType.planningMaterial,
  InfoFeatureType.planningSubmittal
];

export function useBidPackagePlanning(params: {
  featureId: string;
  token: string;
  onRefetchCallback: Function;
}) {
  const { featureId, token, onRefetchCallback } = params;

  const [featureLinks, setFeatureLinks] = useState<LinksForFeature | null>(
    null
  );
  const [loadingLinks, setLoadingLinks] = useState(false);

  const { gqlClientForProject, eventLogs }: TProjectContext =
    useContext(ProjectContext);

  const [applyWorkflowMutation] = useCIQMutation(APPLY_WORKFLOW_MUTATION, {
    client: gqlClientForProject
  });

  const getPlanningData = useCallback(async () => {
    if (!token || !featureId) {
      return null;
    }
    setLoadingLinks(true);
    let linksForAFeature: any = null;
    const PlanningDataResponse = await getGQLQueryData(
      token,
      GET_FEATURE_INSTANCE_PLANNING_DATA,
      {
        featureId
      }
    );

    if (PlanningDataResponse.success) {
      const allLinks = extractTargetFeatureInstances(
        structuredClone(PlanningDataResponse.data.data.feature_instance_by_pk)
      );

      allLinks.forEach((link: any) => {
        linksForAFeature = linksForAFeature || {};
        let formattedMilestones: WorkflowInstanceMilestone[] = [];
        if (link.workflow_instance_milestones) {
          formattedMilestones = formatWFInstanceMilestones(
            link.workflow_instance_milestones
          );
          linksForAFeature[FeatureTypes[link.feature_type_id]] = {
            ...link,
            workflow_instance_milestones: formattedMilestones
          };
        } else {
          linksForAFeature[FeatureTypes[link.feature_type_id]] = link;
        }
      });
    }
    setFeatureLinks(linksForAFeature);
    setLoadingLinks(false);
    return PlanningDataResponse;
  }, [featureId, token]);

  const multiFetchPlanningData = useCallback(async () => {
    getPlanningData();
    setTimeout(() => {
      getPlanningData();
    }, 3000);
    setTimeout(() => {
      getPlanningData();
    }, 6000);
  }, [getPlanningData]);

  const refetchAllData = useCallback(() => {
    onRefetchCallback();
    multiFetchPlanningData();
  }, [multiFetchPlanningData, onRefetchCallback]);

  const updateFeatureWorkflowInstance = useCallback(
    async (updateParams: { input: Array<any> }) => {
      const payload = {
        variables: {
          input: updateParams.input,
          metadata: {
            request_source: "DETAIL_PAGE"
          }
        }
      };
      const updateResponse = await applyWorkflowMutation(payload);
      refetchAllData();
      return updateResponse;
    },
    [applyWorkflowMutation, refetchAllData]
  );

  useEffect(() => {
    getPlanningData();
  }, [getPlanningData]);

  // Poll the changes and update the respective UI components
  const previousEventLogs = useRef(eventLogs);

  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      const conditionForBidAndDesignPackage = (log: TAuditChangeLog) => {
        return (
          allImpactingFeatureTypes.includes(log?.info?.feature_type) &&
          hasTheFeature(featureId, log, featureId)
        );
      };

      if (eventLogs.some(conditionForBidAndDesignPackage)) {
        getPlanningData();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, featureId, getPlanningData]);

  return {
    featureLinks,
    loadingLinks,
    updateFeatureWorkflowInstance,
    refetch: refetchAllData
  };
}
